import { useCallback, useEffect, useContext, useState, useRef } from 'react';
import { Stack, Group, Radio, Box, createStyles } from '@mantine/core';

import { Text } from '~/domains/design-system/Text';
import { toSafeMarkup } from '~/domains/common/utils/toSafeMarkup';
import { Tooltip } from '~/domains/common/components/Tooltip/Tooltip';
import { ContestManageModalsContext } from '../../../components/Modals/ContestManageModalsContext';

import type { StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';
import { getFilteredSelectOptions } from '../utils/getFilteredSelectOptions';
import useHandleTextClick from '../../../hooks/useHandleTextClick';

import type { RadioGroupField, RadioGroupOption } from './types';

const useStyles = createStyles((theme) => ({
  description: {
    color: theme.colors.gray2[7],
    '& a': {
      color: theme.colorScheme === 'dark' ? theme.colors.splash[5] : theme.colors.splash[7],
      textDecoration: 'none',

      '&:hover': {
        textDecoration: 'underline',
      },
    },
  },
  // Default option styles
  optionDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  optionEnabled: {
    cursor: 'pointer',
    opacity: 1,
  },
  // Compact variant styles - only used when params.compact === true
  compactRow: {
    paddingTop: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
    paddingBottom: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
    borderBottom: `1px solid ${theme.colors.gray[1]}`,
  },
  compactHorizontalRow: {
    paddingTop: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
    paddingBottom: /* eslint-disable-line local-rules/use-theme-tokens */ 10,
    borderBottom: `1px solid ${theme.colors.gray[1]}`,
    width: '100%',
  },
  compactFullWidth: {
    width: '100%',
  },
  compactOptionDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
  compactOptionEnabled: {
    cursor: 'pointer',
    opacity: 1,
  },
  compactOptionLabel: {
    whiteSpace: 'nowrap',
  },
}));

export function RadioGroupFieldElement({
  field,
  path = [],
  value,
  onValueChange,
  errors,
  isTouched,
  sectionKey,
  allValues,
  allErrors,
  propagateFieldLabelToParent,
  FieldSwitch,
}: StaticFieldProps<RadioGroupField>) {
  const { classes } = useStyles();
  const [hasActionShowed, setHasActionShowed] = useState<Record<string, boolean>>({});
  const { openModal } = useContext(ContestManageModalsContext);
  const options = getFilteredSelectOptions({
    options: field.options,
    sectionKey,
    allValues,
    allErrors,
  }) as RadioGroupOption[];

  const handleSubLabelClick = useHandleTextClick();

  // Track if we've set the default value to prevent loops
  const hasSetDefaultRef = useRef(false);

  useEffect(() => {
    if (hasSetDefaultRef.current) return;
    if (typeof field.defaultValue !== 'undefined' && typeof value === 'undefined' && !isTouched) {
      hasSetDefaultRef.current = true;
      onValueChange?.(field.defaultValue, true);
    }
  }, [field.defaultValue, value, isTouched, onValueChange]);

  const handleAction = useCallback(
    (option: RadioGroupOption) => {
      if (option.onSelectAction?.actionType === 'openModal') {
        const optionKey = option.value.toString();
        if (option.onSelectAction.showOnlyOnce && hasActionShowed[optionKey]) {
          return;
        }
        openModal(option.onSelectAction.payload);
        setHasActionShowed((prev) => ({ ...prev, [optionKey]: true }));
      }
    },
    [hasActionShowed, openModal]
  );

  const handleOptionChange = useCallback(
    (optionValue: string) => {
      const option = options.find((opt) => opt.value.toString() === optionValue);
      handleAction(option);
      const actualValue = option?.value;
      if (actualValue !== undefined) {
        onValueChange?.(actualValue);
      }
    },
    [onValueChange, options, handleAction]
  );

  const handleOptionClick = useCallback(
    (option: RadioGroupOption) => {
      if (option.isDisabled) return;
      handleAction(option);
      onValueChange?.(option.value);
    },
    [onValueChange, handleAction]
  );

  if (options.length === 0) {
    return null;
  }

  const radioGroupValue = value !== undefined && value !== null ? value.toString() : '';
  const isHorizontal = field.params?.layout === 'horizontal';
  const isCompact = field.params?.compact === true;
  // Field-level disabled/locked - applies to all options (only for compact variant)
  const isFieldDisabled = field.disabled || !!field.locked;

  // Horizontal layout (with optional compact styling)
  if (isHorizontal) {
    // Use compact styles only when compact flag is set
    const optionDisabledClass = isCompact ? classes.compactOptionDisabled : classes.optionDisabled;
    const optionEnabledClass = isCompact ? classes.compactOptionEnabled : classes.optionEnabled;

    return (
      <div className={isCompact ? classes.compactFullWidth : undefined} data-field-key={field.key}>
        <FieldMeta
          info={field?.info}
          field={field}
          errors={errors}
          isTouched={isTouched}
          labelTransform={field.labelTransform}
        />
        <Radio.Group value={radioGroupValue} onChange={handleOptionChange}>
          <Box className={isCompact ? classes.compactHorizontalRow : undefined}>
            <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={field.params?.spacing ?? 40} noWrap>
              {options.map((option) => {
                const isOptionDisabled = isFieldDisabled || option.isDisabled;
                return (
                  <Tooltip
                    key={option.value}
                    isRendered={!!option.params?.tooltip || !!field.locked}
                    withinPortal
                    position="top-start"
                    label={field.locked || option.params?.tooltip}
                  >
                    <Group
                      /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16}
                      align="center"
                      noWrap
                      className={isOptionDisabled ? optionDisabledClass : optionEnabledClass}
                      onClick={() => !isFieldDisabled && handleOptionClick(option)}
                      data-radio-option
                    >
                      <Radio
                        color="blue.8"
                        value={option.value.toString()}
                        disabled={isOptionDisabled}
                        size="xs"
                      />
                      <Text variant="headline-medium">{option.label}</Text>
                    </Group>
                  </Tooltip>
                );
              })}
            </Group>
          </Box>
        </Radio.Group>
      </div>
    );
  }

  // Compact vertical layout - used for value selectors in ModalFieldGroup
  if (isCompact) {
    return (
      <div className={classes.compactFullWidth} data-field-key={field.key}>
        <FieldMeta
          info={field?.info}
          field={field}
          errors={errors}
          isTouched={isTouched}
          labelTransform={field.labelTransform}
        />
        <Radio.Group value={radioGroupValue} onChange={handleOptionChange}>
          <Stack spacing={0} className={classes.compactFullWidth}>
            {options.map((option) => {
              const isOptionDisabled = isFieldDisabled || option.isDisabled;
              return (
                <Box key={option.value} className={classes.compactRow}>
                  <Tooltip
                    isRendered={!!option.params?.tooltip || !!field.locked}
                    withinPortal
                    position="top-start"
                    label={field.locked || option.params?.tooltip}
                  >
                    <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16} align="flex-start" noWrap>
                      <Box data-radio-option>
                        <Radio
                          color="blue.8"
                          value={option.value.toString()}
                          disabled={isOptionDisabled}
                          size="xs"
                          pos="relative"
                          top={2}
                        />
                      </Box>
                      <Stack
                        /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}
                        className={
                          isOptionDisabled
                            ? classes.compactOptionDisabled
                            : classes.compactOptionEnabled
                        }
                        onClick={() => !isFieldDisabled && handleOptionClick(option)}
                        data-radio-option
                      >
                        <Text variant="headline-medium" className={classes.compactOptionLabel}>
                          {option.label}
                        </Text>
                        {option.description && (
                          <Text
                            className={classes.description}
                            onClick={handleSubLabelClick}
                            dangerouslySetInnerHTML={{ __html: toSafeMarkup(option.description) }}
                            variant="body-large"
                            c="gray2.3"
                          />
                        )}
                      </Stack>
                    </Group>
                  </Tooltip>

                  {option.fields && option.fields.length > 0 && (
                    <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24} mt={20} ml={{ base: 0, md: 36 }}>
                      {option.fields.map((fieldKey) => (
                        <FieldSwitch
                          key={fieldKey}
                          fieldKey={fieldKey}
                          sectionKey={sectionKey || ''}
                          path={path.slice(0, -1)}
                          propagateFieldLabelToParent={
                            value === option.value ? propagateFieldLabelToParent : undefined
                          }
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Radio.Group>
      </div>
    );
  }

  // Default layout - exactly as original (no changes from main)
  return (
    <div data-field-key={field.key}>
      <FieldMeta info={field?.info} field={field} errors={errors} isTouched={isTouched} />
      <Radio.Group value={radioGroupValue} onChange={handleOptionChange}>
        <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={field.params?.spacing ?? 40}>
          {options.map((option) => (
            <Box key={option.value}>
              <Tooltip
                isRendered={!!option.params?.tooltip}
                withinPortal
                position="top-start"
                label={option.params?.tooltip}
              >
                <Group /* eslint-disable-line local-rules/use-theme-tokens */ spacing={16} align="flex-start" noWrap>
                  <Box data-radio-option>
                    <Radio
                      color="blue.8"
                      value={option.value.toString()}
                      disabled={option.isDisabled}
                      size="xs"
                      pos="relative"
                      top={2}
                    />
                  </Box>
                  <Stack
                    /* eslint-disable-line local-rules/use-theme-tokens */ spacing={4}
                    className={option.isDisabled ? classes.optionDisabled : classes.optionEnabled}
                    onClick={() => handleOptionClick(option)}
                    data-radio-option
                  >
                    <Text variant="title-large">{option.label}</Text>
                    {option.description && (
                      <Text
                        className={classes.description}
                        onClick={handleSubLabelClick}
                        dangerouslySetInnerHTML={{ __html: toSafeMarkup(option.description) }}
                        variant="body-large"
                        c="gray2.3"
                      />
                    )}
                  </Stack>
                </Group>
              </Tooltip>

              {option.fields && option.fields.length > 0 && (
                <Stack /* eslint-disable-line local-rules/use-theme-tokens */ spacing={24} mt={20} ml={{ base: 0, md: 36 }}>
                  {option.fields.map((fieldKey) => (
                    <FieldSwitch
                      key={fieldKey}
                      fieldKey={fieldKey}
                      sectionKey={sectionKey || ''}
                      path={path.slice(0, -1)}
                      propagateFieldLabelToParent={
                        value === option.value ? propagateFieldLabelToParent : undefined
                      }
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ))}
        </Stack>
      </Radio.Group>
    </div>
  );
}
